import { RUNTIME_BACKEND_URL } from '../lib/backendUrl';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';
import {
  BookOpen, ArrowRight, ArrowLeft, Download, Loader2, FileImage, FileText,
  LayoutGrid, Check, Grid3X3, Trash2, Image, ChevronLeft, ChevronRight,
  AlertTriangle, Copy, Keyboard, Undo2, Redo2, Save,
} from 'lucide-react';
import PageDesigner from './PageDesigner';
import PhotoRetouchEditor from './PhotoRetouchEditor';

const BACKEND_URL = RUNTIME_BACKEND_URL;
const MAX_HISTORY = 10;

export default function PanoramicAlbumWizard({
  isOpen, onClose, albumId, allPhotos, token, customerName
}) {
  const [step, setStep] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingLayout, setIsLoadingLayout] = useState(false);
  const [hasSavedLayout, setHasSavedLayout] = useState(false);

  // Step 1
  const [width, setWidth] = useState('60');
  const [height, setHeight] = useState('30');
  const [pageCount, setPageCount] = useState('10');

  // Step 2 - Retouching
  const [retouchSettings, setRetouchSettings] = useState({
    brightness: 1.0, contrast: 1.0, saturation: 1.0,
    sharpness: 1.0, warmth: 0, skin_smooth: 0
  });
  const [retouchSelected, setRetouchSelected] = useState(new Set());
  const [isRetouching, setIsRetouching] = useState(false);
  const [retouchedPhotos, setRetouchedPhotos] = useState(new Set());

  // Step 3 (was Step 2)
  const [pages, setPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [showGuides, setShowGuides] = useState(true);

  // Step 4 (was Step 3)
  const [outputFormat, setOutputFormat] = useState('jpeg');

  // Undo/Redo history
  const historyRef = useRef([]);
  const historyIdxRef = useRef(-1);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const isRestoringRef = useRef(false);
  const pagesRef = useRef(pages);
  const currentPageRef = useRef(currentPage);
  useEffect(() => { currentPageRef.current = currentPage; }, [currentPage]);

  const customerSelectedPhotos = allPhotos.filter(p => p.is_selected === true);

  const usedPhotoIds = useMemo(() => {
    const ids = new Set();
    pages.forEach(p => p.photos.forEach(ph => ids.add(ph.id)));
    return ids;
  }, [pages]);

  const unplacedPhotos = customerSelectedPhotos.filter(p => !usedPhotoIds.has(p.id));

  // Check for saved layout on open
  useEffect(() => {
    if (isOpen && albumId && token) {
      checkSavedLayout();
    }
    if (!isOpen) {
      setStep(1);
      setPages([]);
      pagesRef.current = [];
      setCurrentPage(0);
      setShowGuides(true);
      setOutputFormat('jpeg');
      setHasSavedLayout(false);
      historyRef.current = [];
      historyIdxRef.current = -1;
      setCanUndo(false);
      setCanRedo(false);
      setRetouchSettings({ brightness: 1.0, contrast: 1.0, saturation: 1.0, sharpness: 1.0, warmth: 0, skin_smooth: 0 });
      setRetouchSelected(new Set());
      setRetouchedPhotos(new Set());
    }
  }, [isOpen, albumId, token]);

  const checkSavedLayout = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/albums/${albumId}/panoramic-layout`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.has_layout) {
          setHasSavedLayout(true);
          setWidth(String(data.layout.width_cm || 60));
          setHeight(String(data.layout.height_cm || 30));
          setPageCount(String(data.layout.page_count || 10));
        }
      }
    } catch (e) { /* ignore */ }
  };

  const loadSavedLayout = async () => {
    setIsLoadingLayout(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/albums/${albumId}/panoramic-layout`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Yüklenemedi');
      const data = await res.json();
      if (data.has_layout && data.layout.pages) {
        const loadedPages = data.layout.pages;
        setWidth(String(data.layout.width_cm || 60));
        setHeight(String(data.layout.height_cm || 30));
        setPageCount(String(data.layout.page_count || loadedPages.length));
        pagesRef.current = loadedPages;
        setPages(loadedPages);
        setCurrentPage(0);
        setStep(3);
        // Initialize history with loaded state
        historyRef.current = [JSON.parse(JSON.stringify(loadedPages))];
        historyIdxRef.current = 0;
        setCanUndo(false);
        setCanRedo(false);
        toast.success('Kaydedilmiş tasarım yüklendi');
      }
    } catch (e) { toast.error('Tasarım yüklenemedi'); }
    finally { setIsLoadingLayout(false); }
  };

  // --- History Management ---
  const commitToHistory = useCallback(() => {
    if (isRestoringRef.current) return;
    const snapshot = JSON.parse(JSON.stringify(pagesRef.current));
    const newHistory = historyRef.current.slice(0, historyIdxRef.current + 1);
    newHistory.push(snapshot);
    if (newHistory.length > MAX_HISTORY) newHistory.shift();
    historyRef.current = newHistory;
    historyIdxRef.current = newHistory.length - 1;
    setCanUndo(historyIdxRef.current > 0);
    setCanRedo(false);
  }, []);

  const undo = useCallback(() => {
    if (historyIdxRef.current <= 0) return;
    isRestoringRef.current = true;
    historyIdxRef.current -= 1;
    const prev = JSON.parse(JSON.stringify(historyRef.current[historyIdxRef.current]));
    pagesRef.current = prev;
    setPages(prev);
    setCanUndo(historyIdxRef.current > 0);
    setCanRedo(historyIdxRef.current < historyRef.current.length - 1);
    setTimeout(() => { isRestoringRef.current = false; }, 50);
  }, []);

  const redo = useCallback(() => {
    if (historyIdxRef.current >= historyRef.current.length - 1) return;
    isRestoringRef.current = true;
    historyIdxRef.current += 1;
    const next = JSON.parse(JSON.stringify(historyRef.current[historyIdxRef.current]));
    pagesRef.current = next;
    setPages(next);
    setCanUndo(historyIdxRef.current > 0);
    setCanRedo(historyIdxRef.current < historyRef.current.length - 1);
    setTimeout(() => { isRestoringRef.current = false; }, 50);
  }, []);

  // Keyboard shortcuts at wizard level
  useEffect(() => {
    if (step !== 2) return;
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        redo();
      } else if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        saveLayout();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [step, undo, redo]);

  // Called by PageDesigner after each completed action
  const handleActionComplete = useCallback(() => {
    commitToHistory();
  }, [commitToHistory]);

  const goToStep2 = () => {
    if (customerSelectedPhotos.length < 1) { toast.error('En az 1 fotograf secili olmali'); return; }
    // Check which photos are already retouched
    const alreadyRetouched = new Set(customerSelectedPhotos.filter(p => p.is_retouched).map(p => p.id));
    setRetouchedPhotos(alreadyRetouched);
    setStep(2);
  };

  const goToStep3 = () => {
    const count = Math.max(1, parseInt(pageCount) || 10);
    const newPages = Array.from({ length: count }, () => ({ photos: [] }));
    pagesRef.current = newPages;
    setPages(newPages);
    setCurrentPage(0);
    setStep(3);
    historyRef.current = [JSON.parse(JSON.stringify(newPages))];
    historyIdxRef.current = 0;
    setCanUndo(false);
    setCanRedo(false);
  };

  const goToStep4 = () => {
    const totalPlaced = pages.reduce((sum, p) => sum + p.photos.length, 0);
    if (totalPlaced === 0) { toast.error('En az bir sayfaya fotograf yerlestirin'); return; }
    setStep(4);
  };

  const toggleRetouchPhoto = (id) => {
    setRetouchSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleRetouchAll = () => {
    if (retouchSelected.size === customerSelectedPhotos.length) {
      setRetouchSelected(new Set());
    } else {
      setRetouchSelected(new Set(customerSelectedPhotos.map(p => p.id)));
    }
  };

  const applyRetouch = async () => {
    if (retouchSelected.size === 0) { toast.error('En az 1 fotograf secin'); return; }
    setIsRetouching(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/albums/${albumId}/retouch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          photo_ids: Array.from(retouchSelected),
          settings: retouchSettings
        })
      });
      if (!res.ok) throw new Error('Rotus uygulanamadi');
      const result = await res.json();
      toast.success(`${result.processed} fotograf rotus uygulandi`);
      setRetouchedPhotos(prev => {
        const next = new Set(prev);
        retouchSelected.forEach(id => next.add(id));
        return next;
      });
    } catch (e) { toast.error(e.message); }
    finally { setIsRetouching(false); }
  };

  const resetRetouch = async () => {
    if (retouchSelected.size === 0) { toast.error('Sifirlamak icin fotograf secin'); return; }
    setIsRetouching(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/albums/${albumId}/retouch/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ photo_ids: Array.from(retouchSelected) })
      });
      if (!res.ok) throw new Error('Sifirlanamadi');
      toast.success('Rotus sifirlandi');
      setRetouchedPhotos(prev => {
        const next = new Set(prev);
        retouchSelected.forEach(id => next.delete(id));
        return next;
      });
    } catch (e) { toast.error(e.message); }
    finally { setIsRetouching(false); }
  };

  const updateCurrentPagePhotos = (photos) => {
    const updated = [...pagesRef.current];
    updated[currentPageRef.current] = { photos };
    pagesRef.current = updated;
    setPages(updated);
  };

  const clearCurrentPage = () => {
    updateCurrentPagePhotos([]);
    toast.info(`Sayfa ${currentPage + 1} temizlendi`);
    commitToHistory();
  };

  const duplicatePage = () => {
    const emptyIdx = pagesRef.current.findIndex((p, i) => i > currentPage && p.photos.length === 0);
    if (emptyIdx === -1) { toast.error('Bos sayfa bulunamadi'); return; }
    const updated = [...pagesRef.current];
    updated[emptyIdx] = { photos: pagesRef.current[currentPage].photos.map(p => ({ ...p })) };
    pagesRef.current = updated;
    setPages(updated);
    toast.success(`Sayfa ${currentPage + 1} → Sayfa ${emptyIdx + 1}`);
    commitToHistory();
  };

  const saveLayout = async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/albums/${albumId}/save-panoramic-layout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          pages,
          width_cm: parseFloat(width),
          height_cm: parseFloat(height),
          page_count: pages.length,
        })
      });
      if (!res.ok) throw new Error('Kaydedilemedi');
      setHasSavedLayout(true);
      toast.success('Tasarım kaydedildi');
    } catch (e) { toast.error(e.message); }
    finally { setIsSaving(false); }
  };

  const generateAlbum = async () => {
    setIsGenerating(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/albums/${albumId}/generate-panoramic`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          pages: pages.map(p => ({ photos: p.photos })),
          width_cm: parseFloat(width), height_cm: parseFloat(height),
          output_format: outputFormat, customer_name: customerName
        })
      });
      if (!response.ok) { const err = await response.json(); throw new Error(err.detail || 'Album olusturulamadi'); }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = customerName.replace(/\u0130/g,'I').replace(/\u0131/g,'i').replace(/\u011e/g,'G').replace(/\u011f/g,'g')
        .replace(/\u00dc/g,'U').replace(/\u00fc/g,'u').replace(/\u015e/g,'S').replace(/\u015f/g,'s')
        .replace(/\u00d6/g,'O').replace(/\u00f6/g,'o').replace(/\u00c7/g,'C').replace(/\u00e7/g,'c').replace(/\s+/g,'_');
      a.download = `panoramik_album_${safeName}.${outputFormat === 'pdf' ? 'pdf' : 'zip'}`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Panoramik album olusturuldu!'); onClose();
    } catch (error) { toast.error(error.message); }
    finally { setIsGenerating(false); }
  };

  const totalPlaced = pages.reduce((sum, p) => sum + p.photos.length, 0);
  const emptyPages = pages.filter(p => p.photos.length === 0).length;
  const photosPerPage = Math.ceil(customerSelectedPhotos.length / (parseInt(pageCount) || 1));

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`${step === 2 || step === 3 ? 'max-w-[98vw] h-[96vh]' : 'max-w-3xl max-h-[90vh]'} overflow-hidden flex flex-col p-0`}>
        {/* Header */}
        <div className="px-5 pt-4 pb-2 flex-shrink-0 border-b">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <BookOpen className="h-4 w-4 text-primary" />
              Panoramik Album Sihirbazi
            </DialogTitle>
          </DialogHeader>
          {/* Progress */}
          <div className="flex items-center justify-center gap-2 pt-2 pb-1">
            {[{ n:1, l:'Ayarlar' }, { n:2, l:'Rotus' }, { n:3, l:'Tasarim' }, { n:4, l:'Indir' }].map(({ n, l }) => (
              <div key={n} className="flex items-center">
                <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${step >= n ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}`}>
                  <span className="w-4 h-4 rounded-full bg-white/20 flex items-center justify-center text-[10px]">{n}</span>
                  {l}
                </div>
                {n < 4 && <ArrowRight className="h-3 w-3 text-muted-foreground mx-1" />}
              </div>
            ))}
          </div>
        </div>

        {/* ===== STEP 1 ===== */}
        {step === 1 && (
          <div className="space-y-6 p-6 overflow-y-auto flex-1">
            {/* Saved layout banner */}
            {hasSavedLayout && (
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-blue-700 dark:text-blue-400 text-sm flex items-center gap-2">
                      <Save className="h-4 w-4" />
                      Kaydedilmiş tasarım bulundu
                    </p>
                    <p className="text-xs text-blue-600/70 dark:text-blue-400/60 mt-0.5">Önceki tasarımınıza kaldığı yerden devam edebilirsiniz</p>
                  </div>
                  <Button size="sm" onClick={loadSavedLayout} disabled={isLoadingLayout} data-testid="load-saved-layout">
                    {isLoadingLayout ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Download className="h-4 w-4 mr-1" />}
                    Tasarımı Yükle
                  </Button>
                </div>
              </div>
            )}

            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
              <div className="flex items-center gap-2 mb-1">
                <Check className="h-4 w-4 text-green-600" />
                <span className="font-medium text-green-700 dark:text-green-400 text-sm">Secilen Fotograflar</span>
              </div>
              <p className="text-2xl font-bold text-green-600" data-testid="selected-count">{customerSelectedPhotos.length} fotograf</p>
              <div className="flex flex-wrap gap-1 mt-2 max-h-14 overflow-y-auto">
                {customerSelectedPhotos.slice(0, 20).map(photo => (
                  <img key={photo.id} src={`${BACKEND_URL}/api/albums/${albumId}/photos/${photo.id}/thumbnail`}
                    alt="" className="w-8 h-8 object-cover rounded border border-green-400" />
                ))}
                {customerSelectedPhotos.length > 20 && (
                  <div className="w-8 h-8 bg-muted rounded flex items-center justify-center text-[10px]">+{customerSelectedPhotos.length - 20}</div>
                )}
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <h3 className="font-medium flex items-center gap-2 text-sm"><LayoutGrid className="h-4 w-4" />Baski Boyutu (cm)</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label htmlFor="pano-height">Yukseklik</Label>
                    <Input id="pano-height" type="number" value={height} onChange={(e) => setHeight(e.target.value)} min="10" max="100" data-testid="height-input" /></div>
                  <div><Label htmlFor="pano-width">Genislik</Label>
                    <Input id="pano-width" type="number" value={width} onChange={(e) => setWidth(e.target.value)} min="10" max="100" data-testid="width-input" /></div>
                </div>
                <p className="text-xs text-muted-foreground">Sayfa: {height} x {width} cm</p>
              </div>
              <div className="space-y-3">
                <h3 className="font-medium flex items-center gap-2 text-sm"><BookOpen className="h-4 w-4" />Sayfa Sayisi</h3>
                <Input type="number" value={pageCount} onChange={(e) => setPageCount(e.target.value)} min="1" max="50" data-testid="page-count-input" />
                <p className="text-xs text-muted-foreground">Sayfa basina ~{photosPerPage} fotograf</p>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={onClose}>Iptal</Button>
              <Button onClick={goToStep2} disabled={customerSelectedPhotos.length < 1} data-testid="step1-continue">
                Devam Et <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* ===== STEP 2: ROTUS ===== */}
        {step === 2 && (
          <PhotoRetouchEditor
            photos={customerSelectedPhotos}
            albumId={albumId}
            token={token}
            onComplete={goToStep3}
            onSkip={goToStep3}
            onBack={() => setStep(1)}
          />
        )}

        {/* ===== STEP 3: MANUAL DESIGN ===== */}
        {step === 3 && pages.length > 0 && (
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Toolbar */}
            <div className="flex items-center justify-between px-3 py-1.5 border-b flex-shrink-0 bg-muted/30">
              <div className="flex items-center gap-1.5">
                <Button variant="outline" size="sm" className="h-7" onClick={() => currentPage > 0 && setCurrentPage(currentPage - 1)} disabled={currentPage === 0}>
                  <ChevronLeft className="h-3.5 w-3.5" />
                </Button>
                <span className="text-xs font-medium min-w-[80px] text-center" data-testid="page-indicator">
                  Sayfa {currentPage + 1} / {pages.length}
                </span>
                <Button variant="outline" size="sm" className="h-7" onClick={() => currentPage < pages.length - 1 && setCurrentPage(currentPage + 1)} disabled={currentPage === pages.length - 1}>
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-[10px] text-muted-foreground mr-1">{pages[currentPage]?.photos.length || 0} foto</span>
                {/* Undo/Redo */}
                <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={undo} disabled={!canUndo}
                  title="Geri Al (Ctrl+Z)" data-testid="undo-btn">
                  <Undo2 className="h-3.5 w-3.5" />
                </Button>
                <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={redo} disabled={!canRedo}
                  title="Ileri Al (Ctrl+Y)" data-testid="redo-btn">
                  <Redo2 className="h-3.5 w-3.5" />
                </Button>
                <div className="border-l mx-1 h-5" />
                <Button variant={showGuides ? 'default' : 'outline'} size="sm" className="h-7 text-xs"
                  onClick={() => setShowGuides(!showGuides)} data-testid="toggle-guides">
                  <Grid3X3 className="h-3 w-3 mr-1" />Kılavuz
                </Button>
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={clearCurrentPage} data-testid="clear-page">
                  <Trash2 className="h-3 w-3 mr-1" />Temizle
                </Button>
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={duplicatePage}>
                  <Copy className="h-3 w-3 mr-1" />Kopyala
                </Button>
                <div className="border-l mx-1 h-5" />
                {/* Save */}
                <Button size="sm" className="h-7 text-xs bg-green-600 hover:bg-green-700 text-white" onClick={saveLayout} disabled={isSaving}
                  title="Kaydet (Ctrl+S)" data-testid="save-layout-btn">
                  {isSaving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}
                  Kaydet
                </Button>
                <div className="border-l mx-1 h-5" />
                <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                  <Keyboard className="h-3 w-3" /> Ctrl+Z: Geri | Ctrl+Y: Ileri
                </span>
              </div>
            </div>

            {/* Main: Pages sidebar + Canvas */}
            <div className="flex-1 flex min-h-0 overflow-hidden">
              {/* LEFT: Page thumbnails */}
              <div className="w-28 flex-shrink-0 border-r bg-muted/20 overflow-y-auto p-2 space-y-2">
                {pages.map((page, idx) => (
                  <div
                    key={idx}
                    onClick={() => setCurrentPage(idx)}
                    className={`cursor-pointer rounded border-2 overflow-hidden relative bg-white transition-all ${
                      idx === currentPage
                        ? 'border-primary ring-1 ring-primary/30 shadow'
                        : page.photos.length > 0
                          ? 'border-green-300 hover:border-primary/50'
                          : 'border-gray-200 hover:border-gray-300'
                    }`}
                    style={{ aspectRatio: `${parseFloat(width) || 60} / ${parseFloat(height) || 30}` }}
                    data-testid={`page-thumb-${idx}`}
                  >
                    {page.photos.map((photo, pIdx) => (
                      <div key={pIdx} className="absolute overflow-hidden"
                        style={{ left: `${photo.x}%`, top: `${photo.y}%`, width: `${photo.w}%`, height: `${photo.h}%` }}>
                        <img src={`${BACKEND_URL}/api/albums/${albumId}/photos/${photo.id}/thumbnail`}
                          alt="" className="w-full h-full object-cover" />
                      </div>
                    ))}
                    <span className={`absolute bottom-0 left-0 right-0 text-center text-[9px] py-px ${
                      idx === currentPage ? 'bg-primary text-white' : page.photos.length > 0 ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-500'
                    }`}>{idx + 1}</span>
                  </div>
                ))}
              </div>

              {/* CENTER: Page canvas - fits exactly */}
              <div className="flex-1 flex items-center justify-center bg-gray-100 dark:bg-gray-800/50 p-2 overflow-hidden"
                onDragOver={(e) => e.preventDefault()}>
                <PageDesigner
                  pageWidthCm={parseFloat(width) || 60}
                  pageHeightCm={parseFloat(height) || 30}
                  placedPhotos={pages[currentPage]?.photos || []}
                  onPlacedPhotosChange={updateCurrentPagePhotos}
                  albumId={albumId}
                  showGuides={showGuides}
                  onActionComplete={handleActionComplete}
                />
              </div>
            </div>

            {/* BOTTOM: Photo strip */}
            <div className="flex-shrink-0 border-t bg-muted/20">
              <div className="flex items-center px-2 py-1 gap-2">
                <span className="text-[10px] text-muted-foreground flex-shrink-0 w-14">
                  <span className="text-green-600 font-medium">{usedPhotoIds.size}</span>/{customerSelectedPhotos.length}
                </span>
                <div className="flex-1 flex gap-2 overflow-x-auto py-1.5" data-testid="photo-strip">
                  {customerSelectedPhotos.map(photo => {
                    const isUsed = usedPhotoIds.has(photo.id);
                    return (
                      <div
                        key={photo.id}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData('application/json', JSON.stringify({ id: photo.id }));
                          e.dataTransfer.effectAllowed = 'copy';
                        }}
                        className={`flex-shrink-0 w-20 h-20 rounded overflow-hidden cursor-grab active:cursor-grabbing border-2 transition-all ${
                          isUsed ? 'border-green-400 opacity-50' : 'border-transparent hover:border-primary/50 hover:shadow'
                        }`}
                        data-testid={`strip-photo-${photo.id}`}
                      >
                        <div className="relative w-full h-full">
                          <img src={`${BACKEND_URL}/api/albums/${albumId}/photos/${photo.id}/thumbnail`}
                            alt="" className="w-full h-full object-cover" draggable={false} />
                          {isUsed && (
                            <div className="absolute top-0 right-0 bg-green-500 rounded-bl w-4 h-4 flex items-center justify-center">
                              <Check className="h-2 w-2 text-white" />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Bottom buttons */}
            <div className="flex items-center justify-between px-4 py-2 border-t flex-shrink-0">
              <Button variant="outline" size="sm" onClick={() => setStep(2)}>
                <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Geri
              </Button>
              <div className="flex items-center gap-2">
                {emptyPages > 0 && (
                  <span className="text-[10px] text-amber-600 flex items-center gap-0.5">
                    <AlertTriangle className="h-3 w-3" />{emptyPages} bos
                  </span>
                )}
                {unplacedPhotos.length > 0 && (
                  <span className="text-[10px] text-amber-600 flex items-center gap-0.5">
                    <Image className="h-3 w-3" />{unplacedPhotos.length} kalan
                  </span>
                )}
                <Button size="sm" onClick={goToStep4} data-testid="step3-continue">
                  Devam Et <ArrowRight className="h-3.5 w-3.5 ml-1" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ===== STEP 4 ===== */}
        {step === 4 && (
          <div className="space-y-5 p-6 overflow-y-auto flex-1">
            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-5 text-center">
              <Check className="h-10 w-10 text-green-600 mx-auto mb-3" />
              <h3 className="text-lg font-medium mb-3">Album Hazir!</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-left max-w-xl mx-auto">
                {[
                  { label: 'Boyut', value: `${height} x ${width} cm` },
                  { label: 'Sayfa', value: `${pages.length}` },
                  { label: 'Fotograf', value: `${totalPlaced}` },
                  { label: 'Bos Sayfa', value: `${emptyPages}`, warn: emptyPages > 0 },
                ].map(({ label, value, warn }) => (
                  <div key={label} className="bg-white dark:bg-gray-800 rounded p-2.5">
                    <p className="text-[10px] text-muted-foreground">{label}</p>
                    <p className={`font-medium text-sm ${warn ? 'text-amber-600' : ''}`}>{value}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="font-medium flex items-center gap-2 text-sm"><FileImage className="h-4 w-4" />Cikti Formati</h3>
              <div className="flex gap-3">
                <Button variant={outputFormat === 'pdf' ? 'default' : 'outline'} onClick={() => setOutputFormat('pdf')} className="flex-1" data-testid="format-pdf">
                  <FileText className="h-4 w-4 mr-2" />PDF
                </Button>
                <Button variant={outputFormat === 'jpeg' ? 'default' : 'outline'} onClick={() => setOutputFormat('jpeg')} className="flex-1" data-testid="format-jpeg">
                  <FileImage className="h-4 w-4 mr-2" />JPEG (ZIP)
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {outputFormat === 'pdf' ? 'Tum sayfalar tek PDF dosyasinda.' : 'Her sayfa ayri JPEG olarak ZIP arsivinde.'}
              </p>
            </div>

            {/* Mini page previews */}
            <div className="space-y-2">
              <h3 className="font-medium text-sm">Sayfa Onizleme</h3>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {pages.map((page, idx) => (
                  <div key={idx} className="flex-shrink-0 rounded border bg-white relative overflow-hidden"
                    style={{ width: '100px', aspectRatio: `${parseFloat(width) || 60} / ${parseFloat(height) || 30}` }}>
                    {page.photos.map((photo, pIdx) => (
                      <div key={pIdx} className="absolute overflow-hidden"
                        style={{ left: `${photo.x}%`, top: `${photo.y}%`, width: `${photo.w}%`, height: `${photo.h}%` }}>
                        <img src={`${BACKEND_URL}/api/albums/${albumId}/photos/${photo.id}/thumbnail`}
                          alt="" className="w-full h-full object-cover" />
                      </div>
                    ))}
                    <span className="absolute bottom-0 right-0 bg-black/50 text-white text-[8px] px-1 rounded-tl">{idx + 1}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-between gap-3 pt-3">
              <Button variant="outline" onClick={() => setStep(3)}>
                <ArrowLeft className="h-4 w-4 mr-2" /> Geri
              </Button>
              <Button onClick={generateAlbum} disabled={isGenerating} size="lg" data-testid="download-btn">
                {isGenerating
                  ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Olusturuluyor...</>
                  : <><Download className="h-4 w-4 mr-2" />Albumu Indir</>}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
